import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  AiBrandProduct,
  AiBrandProfileResponse,
  AiBrandQuestion,
  AiBrandValue,
} from '../../types/ai';

const TEXTAREA_CLASS =
  'w-full min-h-[84px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm font-[inherit] disabled:bg-bg disabled:text-muted';
const MAX_PRODUCTS = 6;

type Draft = Record<string, AiBrandValue>;

const EMPTY_PRODUCT: AiBrandProduct = { name: '', what: '', facts: '', never: '' };

function toLines(list: readonly string[]): string {
  return list.join('\n');
}

function fromLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
}

function asText(value: AiBrandValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function asStrings(value: AiBrandValue | undefined): string[] {
  return Array.isArray(value)
    ? (value as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];
}

function asProducts(value: AiBrandValue | undefined): AiBrandProduct[] {
  return Array.isArray(value)
    ? (value as unknown[]).filter((v): v is AiBrandProduct => typeof v === 'object' && v !== null)
    : [];
}

/** What goes to the API for one question (lists are stored one item per line in the form). */
function fieldValue(q: AiBrandQuestion, value: AiBrandValue | undefined): AiBrandValue {
  if (q.type === 'list') return asStrings(value);
  if (q.type === 'products') return asProducts(value);
  if (q.multiple) return asStrings(value);
  return asText(value);
}

interface FieldProps {
  q: AiBrandQuestion;
  value: AiBrandValue | undefined;
  /** For list fields the raw text the user is typing, so blank lines survive while typing. */
  listText: string;
  disabled: boolean;
  onChange: (value: AiBrandValue) => void;
  onListText: (text: string) => void;
}

function Field({ q, value, listText, disabled, onChange, onListText }: FieldProps) {
  const id = `brand-${q.id}`;
  const help = (
    <p id={`${id}-help`} className="m-0 text-xs text-muted">
      {q.help}
    </p>
  );

  if (q.type === 'choice') {
    const picked = q.multiple ? asStrings(value) : [asText(value)];
    return (
      <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0" aria-describedby={`${id}-help`}>
        <legend className="text-sm font-medium">{q.question}</legend>
        <div className="flex flex-wrap gap-2">
          {(q.options ?? []).map((o) => {
            const on = picked.includes(o.value);
            return (
              <label
                key={o.value}
                className={`inline-flex cursor-pointer items-center rounded-full border px-3 py-1 text-sm focus-within:ring-2 focus-within:ring-accent ${
                  on
                    ? 'border-accent bg-accent-soft font-medium text-link'
                    : 'border-border text-muted'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <input
                  type={q.multiple ? 'checkbox' : 'radio'}
                  name={id}
                  className="sr-only"
                  checked={on}
                  disabled={disabled}
                  onChange={() =>
                    q.multiple
                      ? onChange(on ? picked.filter((p) => p !== o.value) : [...picked, o.value])
                      : onChange(o.value)
                  }
                />
                {o.label}
              </label>
            );
          })}
        </div>
        {help}
      </fieldset>
    );
  }

  if (q.type === 'products') {
    const products = asProducts(value);
    const setProduct = (i: number, patch: Partial<AiBrandProduct>) =>
      onChange(products.map((p, j) => (j === i ? { ...p, ...patch } : p)));
    return (
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0" aria-describedby={`${id}-help`}>
        <legend className="text-sm font-medium">{q.question}</legend>
        {help}
        {products.map((p, i) => (
          <div
            key={i}
            className="grid grid-cols-2 max-sm:grid-cols-1 gap-x-3 gap-y-1.5 rounded-xl border border-border p-3"
          >
            <div className="col-span-2 max-sm:col-span-1 text-xs font-semibold text-muted">{`Product ${i + 1}`}</div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-${i}-name`}>{`Product ${i + 1}: name`}</Label>
              <Input
                id={`${id}-${i}-name`}
                value={p.name}
                disabled={disabled}
                maxLength={80}
                onChange={(e) => setProduct(i, { name: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-${i}-what`}>{`Product ${i + 1}: what it is`}</Label>
              <Input
                id={`${id}-${i}-what`}
                value={p.what}
                disabled={disabled}
                maxLength={160}
                onChange={(e) => setProduct(i, { what: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5 col-span-2 max-sm:col-span-1">
              <Label
                htmlFor={`${id}-${i}-facts`}
              >{`Product ${i + 1}: facts to state exactly`}</Label>
              <Input
                id={`${id}-${i}-facts`}
                value={p.facts}
                disabled={disabled}
                maxLength={240}
                onChange={(e) => setProduct(i, { facts: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5 col-span-2 max-sm:col-span-1">
              <Label htmlFor={`${id}-${i}-never`}>{`Product ${i + 1}: claims never to make`}</Label>
              <Input
                id={`${id}-${i}-never`}
                value={p.never}
                disabled={disabled}
                maxLength={160}
                onChange={(e) => setProduct(i, { never: e.target.value })}
              />
            </div>
            {!disabled ? (
              <Button
                variant="ghost"
                className="col-span-2 max-sm:col-span-1 justify-self-end"
                aria-label={`Remove product ${i + 1}`}
                onClick={() => onChange(products.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            ) : null}
          </div>
        ))}
        {!disabled && products.length < MAX_PRODUCTS ? (
          <Button
            variant="secondary"
            className="self-start"
            onClick={() => onChange([...products, { ...EMPTY_PRODUCT }])}
          >
            Add a product
          </Button>
        ) : null}
      </fieldset>
    );
  }

  const label =
    q.type === 'list' ? `${q.question} (one per line, up to ${q.maxItems ?? 0})` : q.question;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {q.type === 'list' || (q.maxLength ?? 0) > 120 ? (
        <textarea
          id={id}
          className={TEXTAREA_CLASS}
          value={q.type === 'list' ? listText : asText(value)}
          disabled={disabled}
          aria-describedby={`${id}-help`}
          onChange={(e) =>
            q.type === 'list' ? onListText(e.target.value) : onChange(e.target.value)
          }
        />
      ) : (
        <Input
          id={id}
          value={asText(value)}
          disabled={disabled}
          maxLength={q.maxLength}
          aria-describedby={`${id}-help`}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {help}
    </div>
  );
}

/**
 * K7: the brand questionnaire on /indstillinger/ai. Fixed questions for every
 * company, saved per section by an editor or admin. Saving only writes the
 * profile: it never switches the assistant on and never calls the model.
 */
export function BrandProfile() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ai-brand-profile'],
    queryFn: () => apiFetch<AiBrandProfileResponse>('/ai/brand-profile'),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [lists, setLists] = useState<Record<string, string>>({});
  const [savedSection, setSavedSection] = useState<string | null>(null);
  const [active, setActive] = useState<string>('');
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);

  function load(profile: Draft, questions: AiBrandQuestion[], only?: string) {
    setDraft((d) => ({ ...(d ?? {}), ...pick(profile, questions, only) }));
    setLists((l) => {
      const next = { ...l };
      for (const q of questions) {
        if (q.type === 'list' && (only === undefined || q.section === only)) {
          next[q.id] = toLines(asStrings(profile[q.id]));
        }
      }
      return next;
    });
  }

  function pick(profile: Draft, questions: AiBrandQuestion[], only?: string): Draft {
    const out: Draft = {};
    for (const q of questions) {
      if (only === undefined || q.section === only) out[q.id] = fieldValue(q, profile[q.id]);
    }
    return out;
  }

  useEffect(() => {
    if (data && draft === null) load(data.profile, data.questions);
    // First load only: later refreshes must not overwrite what is being typed.
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (input: { section: string; profile: Draft }) =>
      apiFetch<AiBrandProfileResponse>('/ai/brand-profile', {
        method: 'PUT',
        body: { profile: input.profile },
      }),
    onSuccess: (res, input) => {
      queryClient.setQueryData(['ai-brand-profile'], res);
      load(res.profile, res.questions, input.section);
      setSavedSection(input.section);
    },
  });

  if (isLoading || !data || !draft) {
    return <div className="text-muted">Loading brand profile…</div>;
  }

  function saveSection(section: string) {
    if (!data || !draft) return;
    const profile: Draft = {};
    for (const q of data.questions) {
      if (q.section !== section) continue;
      profile[q.id] = q.type === 'list' ? fromLines(lists[q.id] ?? '') : fieldValue(q, draft[q.id]);
    }
    setSavedSection(null);
    saveMutation.mutate({ section, profile });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(data!.promptText);
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  }

  const sectionProgress = (id: string) => {
    const qs = data.questions.filter((q) => q.section === id && q.countsInProgress);
    return { done: qs.filter((q) => hasAnswer(data.profile[q.id])).length, count: qs.length };
  };
  const { answered, total } = data.progress;
  const pct = total === 0 ? 0 : Math.round((answered / total) * 100);

  const activeId = data.sections.some((x) => x.id === active) ? active : data.sections[0]?.id;

  return (
    <section
      aria-labelledby="brand-profile-title"
      id="brand-profile"
      className="flex flex-col gap-3"
    >
      <Card className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 id="brand-profile-title" className="text-sm font-semibold m-0">
            Brand profile
          </h2>
          <Button variant="secondary" onClick={() => void copy()}>
            Copy for Claude
          </Button>
        </div>
        <p className="m-0 text-xs text-muted">
          The same questions for every company. The answers shape every text the assistant writes,
          and &quot;Copy for Claude&quot; gives you the saved profile as text to paste into Claude
          or any other model. Write about the brand, not about people: no names, emails or order
          numbers. Saving the profile never switches the assistant on.
        </p>
        <p className="m-0 text-sm" role="status">
          {answered} of {total} answered
        </p>
        <div
          role="progressbar"
          aria-label="Brand profile progress"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={answered}
          className="h-1.5 w-full rounded-full bg-border overflow-hidden"
        >
          <div className="h-full bg-accent-hover" style={{ width: `${pct}%` }} />
        </div>
        {copied === 'yes' ? (
          <div role="status" className="text-success-text text-sm">
            Copied. Paste it into Claude.
          </div>
        ) : null}
        {copied === 'failed' ? (
          <Alert>Could not copy. Select the text and copy it by hand.</Alert>
        ) : null}
        {!isEditor ? (
          <Alert>You have read-only access. Ask an editor to fill in the profile.</Alert>
        ) : null}
        {saveMutation.isError ? <Alert>Could not save this section. Try again.</Alert> : null}
      </Card>

      <div className="grid grid-cols-[220px_minmax(0,1fr)] max-[860px]:grid-cols-1 gap-4 items-start">
        <nav aria-label="Brand profile sections">
          <ul className="m-0 flex list-none flex-col gap-1 p-0 max-[860px]:flex-row max-[860px]:flex-wrap">
            {data.sections.map((section) => {
              const { done, count } = sectionProgress(section.id);
              const current = section.id === activeId;
              return (
                <li key={section.id} className="max-[860px]:flex-1">
                  <button
                    type="button"
                    id={`brand-tab-${section.id}`}
                    aria-current={current ? 'true' : undefined}
                    aria-controls={`brand-panel-${section.id}`}
                    onClick={() => setActive(section.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-[9px] border px-3 py-2 text-left text-sm ${
                      current
                        ? 'border-accent bg-accent-soft font-medium text-link'
                        : 'border-border text-muted hover:bg-bg'
                    }`}
                  >
                    <span>{section.title}</span>
                    <span className="whitespace-nowrap text-xs">
                      {count === 0 ? 'optional' : `${done} of ${count}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div>
          {data.sections.map((section) => {
            const questions = data.questions.filter((q) => q.section === section.id);
            const { done, count } = sectionProgress(section.id);
            return (
              <Card
                key={section.id}
                id={`brand-panel-${section.id}`}
                role="region"
                aria-labelledby={`brand-heading-${section.id}`}
                hidden={section.id !== activeId}
                className={`p-4 flex-col gap-4 ${section.id === activeId ? 'flex' : 'hidden'}`}
              >
                <h3 id={`brand-heading-${section.id}`} className="text-sm font-semibold m-0">
                  {section.title}{' '}
                  <span className="font-normal text-muted">
                    {count === 0 ? '(optional)' : `(${done} of ${count})`}
                  </span>
                </h3>
                {questions.map((q) => (
                  <Field
                    key={q.id}
                    q={q}
                    value={draft[q.id]}
                    listText={lists[q.id] ?? ''}
                    disabled={!isEditor}
                    onChange={(value) => setDraft((d) => ({ ...(d ?? {}), [q.id]: value }))}
                    onListText={(text) => setLists((l) => ({ ...l, [q.id]: text }))}
                  />
                ))}
                {savedSection === section.id ? (
                  <div role="status" className="text-success-text text-sm">
                    {section.title} saved.
                  </div>
                ) : null}
                {isEditor ? (
                  <Button
                    disabled={saveMutation.isPending}
                    onClick={() => saveSection(section.id)}
                    aria-label={`Save ${section.title}`}
                    className="self-end max-sm:self-stretch"
                  >
                    {saveMutation.isPending && saveMutation.variables?.section === section.id
                      ? 'Saving…'
                      : 'Save section'}
                  </Button>
                ) : null}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function hasAnswer(value: AiBrandValue | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';
}
